import { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import { mapValues } from 'lodash';

import {
  AllTaskProperties,
  PropertiesType,
  TaskParameterValue,
} from '../../types';

/**
 this function unbinds parameters from task properties, its called in more than one place so we put it in a separate function
 *
 * @param {Record<string, AllTaskProperties>} taskProperties - the task properties to unbind parameters from.
 * @param {ParameterDefinition[]} parameters - the parameters to unbind.
 * @param {ParameterDefinition[]} parameterDefinitions - the parameter definitions to use for unbinding.
 * @param {boolean} [checkIfDefaultChanged] - if true, will check if the default value of the parameter has changed before unbinding.
 *
 * @returns {Record<string, AllTaskProperties>} - the task properties with parameters unbound.
 **/
export const unbindParameters = (
  taskProperties: Record<string, AllTaskProperties>,
  parameters: ParameterDefinition[],
  parameterDefinitions: ParameterDefinition[],
  checkIfDefaultChanged?: boolean
) => {
  return mapValues(taskProperties, (task) => {
    if (task.propertiesType !== PropertiesType.Action) {
      // these tasks don't have task parameters, so we should ignore them
      return task;
    }

    const newTaskAppliedParameters = { ...task.taskAppliedParameters };
    const newTaskParameters = { ...task.taskParameters };

    Object.entries(task.taskAppliedParameters ?? {}).forEach(([key, value]) => {
      const updatedParameter = parameters.find((p) => p.id === value);
      // do not change data if the parameter is not assigned for the field
      if (!updatedParameter) return;

      const oldParameter = parameterDefinitions.find((p) => p.id === value);
      // do not change data if the parameter value has not changed, works only for calls where parameters are modified
      if (
        checkIfDefaultChanged &&
        updatedParameter.default === oldParameter?.default
      )
        return;

      delete newTaskAppliedParameters[key];
      newTaskParameters[key] = updatedParameter.default as TaskParameterValue;
    });

    return {
      ...task,
      taskParameters: newTaskParameters,
      taskAppliedParameters: newTaskAppliedParameters,
    };
  });
};
